import { ImageSet } from '@/src/lib/product/types.ts';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { A11y, Keyboard, Navigation, Thumbs } from 'swiper/modules';
import ProductSliderImageItem from '@/src/app/[country]/[lang]/shop/[...segments]/(product-details)/components/ProductImageGallery/ProductSliderImageItem.tsx';
import Swiper from 'swiper';
import { SwiperSlide } from 'swiper/react';
import 'swiper/css';
import Icon from '@/src/app/components/Icon/Icon.tsx';
import { IconName } from '@/src/app/components/Icon/types.ts';
export type ProductImageSliderProps = {
  imageSet: ImageSet;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  thumbsSwiper?: any;
  productName: string;
  initialSlide?: number;
  onActiveIndexChangeHandler?: (newActiveIndex: number) => void;
  imageHeight?: number;
  imageWidth?: number;
  useZoom?: boolean;
  sliderClass: string;
  enableKeyboard?: boolean;
  style?: Record<string, string>;
};
export default function ProductImageSlider({
  imageSet,
  thumbsSwiper,
  productName,
  initialSlide = 0,
  onActiveIndexChangeHandler = () => {},
  imageHeight = 585,
  imageWidth = 585,
  useZoom = true,
  sliderClass,
  enableKeyboard = true,
  style = {}
}: ProductImageSliderProps) {
  const productImageSlider = useRef<Swiper | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const buttonClasses = ' w-[3.125rem]  h-[3.125rem] bg-gray-300 flex justify-center items-center absolute top-1/2 transform -translate-y-1/2 z-10 text-center';
  function onActiveIndexChange(swiperInstance: Swiper): void {
    setActiveIndex(swiperInstance.realIndex);
    onActiveIndexChangeHandler(swiperInstance.realIndex);
  }
  useEffect(() => {
    if (productImageSlider.current || thumbsSwiper === null || thumbsSwiper?.destroyed) return;
    productImageSlider.current = new Swiper(`.${sliderClass}`, {
      modules: [Navigation, Thumbs, Keyboard, A11y],
      loop: true,
      a11y: {
        enabled: true
      },
      keyboard: {
        enabled: enableKeyboard
      },
      navigation: {
        prevEl: '.slider-navigation-prev',
        nextEl: '.slider-navigation-next'
      },
      thumbs: {
        swiper: thumbsSwiper
      },
      lazyPreloadPrevNext: 1,
      on: {
        activeIndexChange: onActiveIndexChange
      },
      initialSlide
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thumbsSwiper]);
  useEffect(() => {
    if (!productImageSlider.current) return;
    if (enableKeyboard) {
      productImageSlider.current.keyboard.enable();
      return;
    }
    productImageSlider.current.keyboard.disable();
  }, [enableKeyboard]);
  return <div className={classNames(sliderClass, 'w-full overflow-hidden')} role="region" aria-label="Carousel" aria-roledescription="carousel" data-testid="image-slider" data-active-index={activeIndex} style={style}>
      <div className="swiper-wrapper">
        {imageSet.images.map((image, index) => {
        return <SwiperSlide key={image.externalUrlSmall}>
              <ProductSliderImageItem url={image.externalUrlLarge} alt={productName} width={imageWidth} height={imageHeight} className="px-[3.125rem]" useZoom={useZoom} priority={index === 0} />
            </SwiperSlide>;
      })}
      </div>
      <div role="group" aria-label="Slide controls">
        <button className={classNames('slider-navigation-prev left-0', buttonClasses)} data-testid="image-slider-prev-arrow">
          <Icon name={IconName.ARROW_BACK_IOS} className="transform translate-x-[5px]" />
        </button>
        <button className={classNames('slider-navigation-next right-0', buttonClasses)} data-testid="image-slider-next-arrow">
          <Icon name={IconName.ARROW_FORWARD_IOS} />
        </button>
      </div>
    </div>;
}